import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useParams } from "react-router-dom";

import carIcon from "../assets/images/car.png";
import gpsIcon from "../assets/images/gps.png";
import { GOOGLE_MAPS_API_KEY } from "../config";

const PUBLIC_API_BASE = process.env.REACT_APP_PUBLIC_RIDE_API_BASE || "";
const DEFAULT_CENTER = { lat: 37.7749, lng: -122.4194 };
const POLL_INTERVAL_MS = 10000;

const loadGoogleMaps = () =>
  new Promise((resolve, reject) => {
    if (window.google?.maps) {
      resolve(window.google.maps);
      return;
    }

    const existingScript = document.querySelector("script[data-google-maps='true']");
    if (existingScript) {
      existingScript.addEventListener("load", () => resolve(window.google.maps));
      existingScript.addEventListener("error", reject);
      return;
    }

    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=geometry,directions`;
    script.async = true;
    script.defer = true;
    script.dataset.googleMaps = "true";
    script.onload = () => resolve(window.google.maps);
    script.onerror = reject;
    document.head.appendChild(script);
  });

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const normalizeLocation = (location) => {
  if (!location) return null;

  const lat = toNumber(location.latitude ?? location.lat);
  const lng = toNumber(location.longitude ?? location.lng ?? location.lon);

  if (lat === null || lng === null) return null;

  return {
    lat,
    lng,
    heading: toNumber(location.heading) ?? 0,
  };
};

const formatEta = (etaMinutes) => {
  if (etaMinutes === null || etaMinutes === undefined || etaMinutes === "") return "ETA unavailable";
  const eta = Number(etaMinutes);
  if (!Number.isFinite(eta)) return "ETA unavailable";
  if (eta <= 1) return "Arriving now";
  return `${Math.round(eta)} min away`;
};

const formatLastUpdated = (value) => {
  if (!value) return "Just now";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Just now";
  return `Updated ${date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}`;
};

const LiveLocation = () => {
  const { shareToken: shareTokenFromParams, rideId: rideIdFromParams } = useParams();
  const location = useLocation();
  const query = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const shareToken =
    shareTokenFromParams ||
    query.get("shareToken") ||
    query.get("token") ||
    rideIdFromParams ||
    query.get("rideId") ||
    query.get("id");

  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const driverMarkerRef = useRef(null);
  const destinationMarkerRef = useRef(null);
  const directionsRendererRef = useRef(null);
  const pollIntervalRef = useRef(null);

  const [mapsReady, setMapsReady] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [trackingData, setTrackingData] = useState(null);

  const shareSession = trackingData?.shareSession || null;
  const ride = trackingData?.ride || null;
  const driver = trackingData?.driver || null;
  const currentLocation = normalizeLocation(trackingData?.location);
  const destinationLocation = useMemo(() => {
    const locationPayload =
      trackingData?.destination ||
      trackingData?.dropoffLocation ||
      trackingData?.dropoff ||
      trackingData?.ride?.dropoffLocation ||
      null;
    return normalizeLocation(locationPayload);
  }, [trackingData]);

  useEffect(() => {
    let isMounted = true;

    loadGoogleMaps()
      .then(() => {
        if (isMounted) setMapsReady(true);
      })
      .catch(() => {
        if (isMounted) {
          setError("We couldn't load the map right now.");
          setLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  useEffect(() => {
    if (!mapsReady || !mapRef.current || mapInstanceRef.current) return;

    const map = new window.google.maps.Map(mapRef.current, {
      center: DEFAULT_CENTER,
      zoom: 13,
      mapTypeId: "roadmap",
      zoomControl: true,
      fullscreenControl: false,
      streetViewControl: false,
      mapTypeControl: false,
      gestureHandling: "greedy",
    });

    mapInstanceRef.current = map;
    directionsRendererRef.current = new window.google.maps.DirectionsRenderer({
      map,
      suppressMarkers: true,
      polylineOptions: {
        strokeColor: "#18B26A",
        strokeWeight: 6,
        strokeOpacity: 0.9,
      },
    });
  }, [mapsReady]);

  useEffect(() => {
    if (!shareToken) {
      setLoading(false);
      setError("This tracking link is invalid.");
      return undefined;
    }

    const fetchTracking = async ({ silent = false } = {}) => {
      if (!silent) setLoading(true);

      try {
        const response = await fetch(
          `${PUBLIC_API_BASE}/public/live-location/${encodeURIComponent(shareToken)}`,
          {
            method: "GET",
            headers: { Accept: "application/json" },
          }
        );

        if (!response.ok) {
          if (response.status === 404 || response.status === 410) {
            throw new Error("This tracking link has expired or is no longer active.");
          }
          throw new Error("We couldn't load this trip right now.");
        }

        const data = await response.json();
        setTrackingData(data);
        setError("");
      } catch (err) {
        setError(err.message || "We couldn't load this trip right now.");
      } finally {
        setLoading(false);
      }
    };

    fetchTracking();
    pollIntervalRef.current = window.setInterval(() => {
      fetchTracking({ silent: true });
    }, POLL_INTERVAL_MS);

    return () => {
      if (pollIntervalRef.current) {
        window.clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [shareToken]);

  useEffect(() => {
    if (!mapInstanceRef.current || !currentLocation) return;

    const map = mapInstanceRef.current;
    if (!driverMarkerRef.current) {
      driverMarkerRef.current = new window.google.maps.Marker({
        position: currentLocation,
        map,
        icon: {
          url: carIcon,
          scaledSize: new window.google.maps.Size(44, 44),
          anchor: new window.google.maps.Point(22, 22),
        },
        zIndex: 1000,
      });
    } else {
      driverMarkerRef.current.setPosition(currentLocation);
    }

    map.panTo(currentLocation);
  }, [currentLocation]);

  useEffect(() => {
    if (!mapInstanceRef.current || !destinationLocation) return;

    const map = mapInstanceRef.current;
    if (!destinationMarkerRef.current) {
      destinationMarkerRef.current = new window.google.maps.Marker({
        position: destinationLocation,
        map,
        icon: {
          url: gpsIcon,
          scaledSize: new window.google.maps.Size(38, 38),
          anchor: new window.google.maps.Point(19, 38),
        },
        title: "Destination",
      });
    } else {
      destinationMarkerRef.current.setPosition(destinationLocation);
    }
  }, [destinationLocation]);

  useEffect(() => {
    if (!window.google?.maps || !directionsRendererRef.current || !currentLocation || !destinationLocation) return;

    const directionsService = new window.google.maps.DirectionsService();
    directionsService.route(
      {
        origin: currentLocation,
        destination: destinationLocation,
        travelMode: window.google.maps.TravelMode.DRIVING,
      },
      (result, status) => {
        if (status === "OK") {
          directionsRendererRef.current.setDirections(result);
        }
      }
    );
  }, [currentLocation, destinationLocation]);

  const isActive = shareSession?.isActive !== false;
  const etaText = formatEta(ride?.etaMinutes);
  const updatedText = formatLastUpdated(ride?.lastUpdatedAt);
  const pageTitle = loading
    ? "Loading ride details"
    : error
      ? "Live tracking unavailable"
      : isActive
        ? "Track this Berymo ride"
        : "Ride sharing has ended";

  return (
    <>
      <style>{`
        *,*::before,*::after{box-sizing:border-box}
        html,body,#root{height:100%}
        body{margin:0;background:#09121a;font-family:Arial,sans-serif}
        .live-page{position:fixed;inset:0;background:radial-gradient(circle at top,#183347 0%,#09121a 58%,#05080c 100%);color:#f8fbff}
        .live-map{position:absolute;inset:0}
        .live-overlay{position:absolute;inset:0;background:linear-gradient(180deg,rgba(6,10,14,0.88) 0%,rgba(6,10,14,0.18) 28%,rgba(6,10,14,0.70) 100%);pointer-events:none}
        .live-panel{position:absolute;top:18px;left:18px;right:18px;display:flex;justify-content:space-between;gap:16px;align-items:flex-start;z-index:2}
        .chip{display:inline-flex;align-items:center;gap:10px;padding:12px 16px;border-radius:999px;background:rgba(8,14,20,0.85);backdrop-filter:blur(12px);box-shadow:0 12px 30px rgba(0,0,0,0.24);font-size:14px;font-weight:700}
        .chip-dot{width:10px;height:10px;border-radius:50%;background:${error ? "#ff6b6b" : isActive ? "#1fdd78" : "#f6c344"}}
        .bottom-card{position:absolute;left:18px;right:18px;bottom:18px;z-index:2;background:rgba(9,18,26,0.90);border:1px solid rgba(255,255,255,0.08);border-radius:28px;padding:20px;box-shadow:0 20px 60px rgba(0,0,0,0.36);backdrop-filter:blur(18px)}
        .eyebrow{font-size:12px;letter-spacing:0.14em;text-transform:uppercase;color:#8fb2c9;margin-bottom:10px}
        .title{font-size:clamp(28px,5vw,40px);font-weight:800;line-height:1.05;margin:0 0 8px}
        .subtitle{font-size:15px;color:#c8d8e3;line-height:1.5;max-width:700px}
        .grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(180px,1fr));gap:14px;margin-top:18px}
        .stat{background:rgba(255,255,255,0.04);border-radius:18px;padding:14px}
        .stat-label{font-size:12px;text-transform:uppercase;letter-spacing:0.12em;color:#86a5ba;margin-bottom:8px}
        .stat-value{font-size:16px;font-weight:700;color:#f7fbff;line-height:1.4}
        .center-state{position:absolute;inset:0;display:flex;align-items:center;justify-content:center;padding:24px;z-index:3}
        .state-card{width:min(520px,100%);background:rgba(9,18,26,0.92);border:1px solid rgba(255,255,255,0.08);border-radius:28px;padding:28px;text-align:center;box-shadow:0 20px 60px rgba(0,0,0,0.36)}
        .state-title{font-size:28px;font-weight:800;margin:0 0 10px}
        .state-text{font-size:15px;line-height:1.6;color:#cae0ef}
        @media (max-width: 720px){
          .live-panel{left:14px;right:14px;top:14px;flex-direction:column;align-items:flex-start}
          .bottom-card{left:14px;right:14px;bottom:14px;padding:18px}
          .title{font-size:28px}
        }
      `}</style>

      <div className="live-page">
        <div ref={mapRef} className="live-map" />
        <div className="live-overlay" />

        {!error && (
          <>
            <div className="live-panel">
              <div className="chip">
                <span className="chip-dot" />
                {isActive ? "Live trip status" : "Trip sharing ended"}
              </div>
              <div className="chip">{updatedText}</div>
            </div>

            <div className="bottom-card">
              <div className="eyebrow">Berymo Ride Tracking</div>
              <h1 className="title">{pageTitle}</h1>
              <div className="subtitle">
                {loading
                  ? "Getting the latest ride details for this trip."
                  : `${etaText}. Follow the trip progress, destination, and driver details in real time.`}
              </div>

              {!loading && (
                <div className="grid">
                  <div className="stat">
                    <div className="stat-label">Driver</div>
                    <div className="stat-value">{driver?.firstName || "Assigned driver"}</div>
                  </div>
                  <div className="stat">
                    <div className="stat-label">Vehicle</div>
                    <div className="stat-value">
                      {[driver?.vehicleColor, driver?.vehicleMake, driver?.vehicleModel].filter(Boolean).join(" ") || "Vehicle details unavailable"}
                    </div>
                  </div>
                  <div className="stat">
                    <div className="stat-label">Plate</div>
                    <div className="stat-value">{driver?.licensePlate || "Unavailable"}</div>
                  </div>
                  <div className="stat">
                    <div className="stat-label">Pickup</div>
                    <div className="stat-value">{ride?.pickup || "Pickup unavailable"}</div>
                  </div>
                  <div className="stat">
                    <div className="stat-label">Drop-off</div>
                    <div className="stat-value">{ride?.dropoff || "Drop-off unavailable"}</div>
                  </div>
                  <div className="stat">
                    <div className="stat-label">Status</div>
                    <div className="stat-value">{ride?.status || "In progress"}</div>
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {(loading || error) && (
          <div className="center-state">
            <div className="state-card">
              <h2 className="state-title">{loading ? "Loading trip" : "Link unavailable"}</h2>
              <div className="state-text">
                {loading
                  ? "Please wait while we fetch the current trip details."
                  : error}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default LiveLocation;
